import { html } from 'lighterhtml';
import { useMemo, useReducer } from 'haunted/lib/core';

import { displayState, StateReducer } from './accordion.types';
import {
  singleClosedStateReducer,
  singleStateReducer,
} from './accordion.reducers';
import { component } from '../../utils';

import './accordion.css';

const defaultStateReducer: StateReducer = (state) => {
  return state;
};

function chooseStateReducer(type: string): StateReducer {
  if (type === 'singleOpen') {
    return singleStateReducer;
  }

  if (type === 'singleOpenClose') {
    return singleClosedStateReducer;
  }

  return defaultStateReducer;
}

interface AccordionProps {
  type?: 'singleOpen' | 'singleOpenClose';
  initialOpenSlides?: string;
}

function Accordion({ initialOpenSlides, type = 'singleOpen' }: AccordionProps) {
  const stateReducer = useMemo(() => chooseStateReducer(type), [type]);
  const [state, dispatch] = useReducer(
    stateReducer,
    {
      openIndexes: [],
    },
    () => {
      try {
        const openIndexes = initialOpenSlides
          ? (JSON.parse(initialOpenSlides) as Array<
              string | number
            >).map((item) => item.toString())
          : [];

        return {
          openIndexes,
        };
      } catch (err) {
        return {
          openIndexes: [],
        };
      }
    }
  );

  // Initialize context
  const contextValue = useMemo(() => {
    return {
      openIndexes: state.openIndexes,
      handleItemClick(index: string) {
        // Handles open/close toggle
        const closing = state.openIndexes.indexOf(index) !== -1;
        const changes = {
          type: closing ? displayState.closing : displayState.opening,
          openIndexes: closing
            ? state.openIndexes.filter((i) => i !== index)
            : [...state.openIndexes, index],
        };
        dispatch(changes);
      },
      closeItem(index: string) {
        // Handles close
        const changes = {
          type: displayState.closing,
          openIndexes: state.openIndexes.filter((i) => i !== index),
        };
        dispatch(changes);
      },
    };
  }, [state]);

  return html`
    <eo-internal-accordion-provider value=${contextValue}>
      <slot></slot>
    </eo-internal-accordion-provider>
  `;
}

export const AccordionElement = component(Accordion, {
  observedAttributes: ['initial-open-slides', 'type'],
});
